import { useSyncExternalStore } from "react";

// Chromium browsers (Chrome and Edge on a computer, Chrome on Android) hand the page a
// `beforeinstallprompt` event once the app is installable. Keeping it lets one tap on our own
// button open the browser's install dialog, instead of telling people to find a menu item.
// Safari and Firefox have no such request (WebKit closed it as WONTFIX, bugs.webkit.org 193959),
// so there the strip keeps saying the one step. The event can be used once.
type InstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferred: InstallPromptEvent | null = null;
let installedHere = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

// The browser can fire this before React renders, so it is caught when this module loads.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installedHere = true;
    deferred = null;
    notify();
  });
}

export type InstallState = { canPrompt: boolean; installedHere: boolean };
let snapshot: InstallState = { canPrompt: false, installedHere: false };
function read(): InstallState {
  const canPrompt = deferred !== null;
  if (snapshot.canPrompt !== canPrompt || snapshot.installedHere !== installedHere) {
    snapshot = { canPrompt, installedHere };
  }
  return snapshot;
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function useInstallPrompt(): InstallState {
  return useSyncExternalStore(subscribe, read, read);
}

/** Opens the browser's own install dialog. */
export async function installApp(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const event = deferred;
  if (!event) return "unavailable";
  deferred = null;
  notify();
  await event.prompt();
  return (await event.userChoice).outcome;
}

/** The one step to show where no browser request exists. */
export function installStep(): string {
  const agent = navigator.userAgent;
  // iPadOS asks for the desktop site and says Macintosh; a touch screen tells it apart.
  const ios = /iPhone|iPad|iPod/.test(agent) || (/Macintosh/.test(agent) && navigator.maxTouchPoints > 1);
  if (ios) return "Tap Share, then Add to Home Screen.";
  if (/Macintosh/.test(agent) && /Safari\//.test(agent) && !/Chrome|Chromium|Edg\/|Firefox/.test(agent)) {
    return "In Safari, choose File, then Add to Dock.";
  }
  return "iPhone: Share → Add to Home Screen. Android/Chrome: menu → Install app.";
}
